import { useState, useEffect } from 'react';
import { whatsappApi } from '../../../services/api';
import { toast } from 'react-hot-toast';
import { Loader2, RefreshCw, QrCode, CheckCircle, XCircle, Smartphone } from 'lucide-react';
import { usePageTitle } from '../../../hooks/usePageTitle';

export default function ConnectionTab() {
  usePageTitle('Conexão WhatsApp');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [connection, setConnection] = useState<any>(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    checkConnection();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    // Poll every 5 seconds when connecting or QR ready
    if (connection && ['connecting', 'qr_ready'].includes(connection.status)) {
      setPolling(true);
      interval = setInterval(() => {
        checkConnection(true); // Silent check
      }, 5000);
    } else {
      setPolling(false);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [connection]);

  const checkConnection = async (silent = false) => {
    if (!silent) setLoading(true);
    
    try {
      const response = await whatsappApi.getConnectionStatus();
      setConnection(response);
    } catch (error: any) {
      if (!silent) {
        toast.error(error.error || 'Erro ao verificar conexão');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleGenerateQR = async () => {
    setGenerating(true);
    
    try {
      await whatsappApi.generateQRCode();
      toast.success('QR Code solicitado com sucesso');
      
      // Start checking for QR code
      setTimeout(() => {
        checkConnection();
      }, 2000);
    } catch (error: any) {
      toast.error(error.error || 'Erro ao gerar QR Code');
    } finally {
      setGenerating(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar?')) return;
    
    setLoading(true);
    
    try {
      await whatsappApi.disconnect();
      toast.success('Desconectado com sucesso');
      checkConnection();
    } catch (error: any) {
      toast.error(error.error || 'Erro ao desconectar');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <div className="bg-white">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Status da Conexão</h2>
          <button
            onClick={() => checkConnection()}
            disabled={polling}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-5 w-5 ${polling ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {!connection?.exists && (
          <div className="text-center py-8">
            <Smartphone className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhuma conexão configurada
            </h3>
            <p className="text-gray-600 mb-6">
              Configure uma conexão com o WhatsApp para enviar mensagens
            </p>
            <button
              onClick={handleGenerateQR}
              disabled={generating}
              className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {generating ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <QrCode className="h-5 w-5 mr-2" />
                  Conectar WhatsApp
                </>
              )}
            </button>
          </div>
        )}

        {connection?.exists && (
          <div className="space-y-4">
            {/* Status Badge */}
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">Status:</span>
              {connection.status === 'connected' && (
                <span className="flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Conectado
                </span>
              )}
              {connection.status === 'connecting' && (
                <span className="flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Conectando...
                </span>
              )}
              {connection.status === 'qr_ready' && (
                <span className="flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  <QrCode className="h-4 w-4 mr-1" />
                  QR Code Pronto
                </span>
              )}
              {['disconnected', 'not_found'].includes(connection.status) && (
                <span className="flex items-center px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                  <XCircle className="h-4 w-4 mr-1" />
                  Desconectado
                </span>
              )}
            </div>

            {/* QR Code Display */}
            {connection.status === 'qr_ready' && connection.qrCode && (
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Escaneie o QR Code com o WhatsApp para conectar:
                </p>
                <div className="inline-block p-4 bg-white border-2 border-gray-300 rounded-lg">
                  <img 
                    src={connection.qrCode} 
                    alt="QR Code" 
                    className="w-64 h-64"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  Atualizando automaticamente a cada 5 segundos...
                </p>
              </div>
            )}

            {/* Connected User Info */}
            {connection.status === 'connected' && connection.user && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <h3 className="font-medium text-green-900 mb-2">Conta Conectada:</h3>
                <div className="text-sm text-green-800 space-y-1">
                  <p><strong>Nome:</strong> {connection.user.pushname || connection.user.name || 'N/A'}</p>
                  <p><strong>Número:</strong> {connection.user.id?.split(':')[0] || 'N/A'}</p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-3 mt-6">
              {['disconnected', 'not_found'].includes(connection.status) && (
                <button
                  onClick={handleGenerateQR}
                  disabled={generating}
                  className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Gerando...
                    </>
                  ) : (
                    <>
                      <QrCode className="h-5 w-5 mr-2" />
                      Reconectar
                    </>
                  )}
                </button>
              )}
              
              {connection.status === 'connected' && (
                <button
                  onClick={handleDisconnect}
                  className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <XCircle className="h-5 w-5 mr-2" />
                  Desconectar
                </button>
              )}
            </div>

            {/* Polling Indicator */}
            {polling && (
              <p className="text-xs text-center text-gray-500 mt-4">
                Verificando status automaticamente...
              </p>
            )}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
        <h3 className="font-medium text-blue-900 mb-3">Como conectar:</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
          <li>Clique em "Conectar WhatsApp" para gerar um QR Code</li>
          <li>Abra o WhatsApp no seu celular</li>
          <li>Toque em "Dispositivos vinculados" no menu</li>
          <li>Escaneie o QR Code exibido na tela</li>
          <li>Aguarde a confirmação da conexão</li>
        </ol>
        <p className="text-xs text-blue-600 mt-4">
          <strong>Importante:</strong> Mantenha o WhatsApp conectado para enviar mensagens.
        </p>
      </div>
    </div>
  );
}
